import { BrowserRouter } from 'react-router-dom';
import AppLayout from './layouts/frontend/AppLayout';
import { Route, Routes } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import 'react-date-picker/dist/DatePicker.css';
import 'react-calendar/dist/Calendar.css';
import routes from './routes/routes';
import React from 'react';
import { useTheme } from './contexts/ThemeContext';
import { addOpacityToHexColor } from './utils/functions';
import PageTracker from './layouts/frontend/PageTracker';
import { setUtmParams } from './analytics';
import AnalyticsInitializer from './analytics/AnalyticsInitializer';
import { useEntities } from './contexts/EntitiesContext';

function App() {
  const { theme } = useTheme();
  const entities = useEntities();
  const { quote } = entities;

  const needsBasename = () => {
    const location = window.location;

    const needsBasenameArray = [
      'distributors.habit.io',
      'distributors.qa.habit.io',
      'distributors.stg.habit.io',
      'distributors.integrations.habit.io',
    ];

    return needsBasenameArray.includes(location.hostname);
  };

  const hasFontAvailable = (data) => {
    return data !== null && data !== undefined && data !== '';
  };

  const { hasSlimStyle, border_radius, colors, fonts, CONFIG } = theme;
  const { BASENAME } = CONFIG;

  let analyticsKey = CONFIG.ANALYTICS_KEY;

  const getAnalyticsKey = () => {
    const hostname = window.location.hostname;
    const DEFAULT_KEY = {
      localhost: 'G-7L6WVV48RD',
      int: 'G-7L6WVV48RD',
      qa: 'G-7L6WVV48RD',
      sb: 'G-0CPRMF0P74',
      prod: 'G-6Q29WSWQXD',
    };
    const env = hostname.includes('localhost')
      ? 'localhost'
      : hostname.includes('integrations')
      ? 'int'
      : hostname.includes('qa')
      ? 'qa'
      : hostname.includes('sandbox')
      ? 'sb'
      : 'prod';

    return DEFAULT_KEY[env];
  };

  const queryParams = new URLSearchParams(window.location.search);
  const utmParams = {
    utm_source: queryParams.get('utm_source'),
    utm_medium: queryParams.get('utm_medium'),
    utm_content: queryParams.get('utm_content'),
    utm_term: queryParams.get('utm_term'),
    utm_campaign: queryParams.get('utm_campaign'),
    client_id: queryParams.get('client_id'),
    analytics_consent: queryParams.get('analytics_consent'),
  };

  Object.keys(utmParams).forEach((key) => {
    if (utmParams[key] === null || utmParams[key] === undefined) {
      delete utmParams[key];
    }
  });
  if (Object.keys(utmParams).length > 0) setUtmParams(utmParams);

  const didUserBlockAnalytics =
    utmParams?.analytics_consent === 'false' ||
    utmParams?.analytics_consent === false;

  const appStyleVariables = {
    '--color-primary': colors['--color-primary'],
    '--color-secondary': colors['--color-secondary'],
    '--color-header-text': colors['--color-header-text'],
    '--color-tile-title': colors['--color-tile-title'],
    '--color-tile-text': colors['--color-tile-text'],
    '--color-tile-shape': colors['--color-tile-shape'],
    '--color-links': colors['--color-links'],
    '--color-radiobuttons-checkboxes':
      colors['--color-radiobuttons-checkboxes'],
    '--color-box-shadow': addOpacityToHexColor(
      colors['--color-radiobuttons-checkboxes'],
      15
    ),

    '--border-radius-button': border_radius.button,
    '--border-radius-tile': border_radius.tile,
    '--border-radius-background': border_radius.background
      ? border_radius.background
      : '0px',

    '--font-primary': 'Regular',
    '--font-bold': hasFontAvailable(fonts.bold) ? 'Bold' : 'Regular',
    // prettier-ignore
    '--font-medium': hasFontAvailable(fonts.medium) ? 'Medium' : 'Regular',
    '--font-light': hasFontAvailable(fonts.light) ? 'Light' : 'Regular',

    '--padding-left-content': hasSlimStyle ? '20px 20px 0px' : '85px 100px 0px',
    '--padding-box-wrapper': hasSlimStyle ? '30px 20px' : '25px 65px',
    '--padding-vertical-text-wrapper': hasSlimStyle ? '30px' : '50px',
    '--padding-right-container': hasSlimStyle
      ? '20px 10px 20px 10px'
      : '20px 15px 20px 15px',
    '--min-width-right-content': hasSlimStyle ? '330px' : '400px',
  };

  return (
    <>
      <BrowserRouter basename={needsBasename() ? '/' + BASENAME : '/'}>
        {!didUserBlockAnalytics && (
          <>
            <AnalyticsInitializer
              analyticsKey={
                analyticsKey === '<-ANALYTICS-KEY->'
                  ? getAnalyticsKey()
                  : analyticsKey
              }
            />
            <PageTracker analyticsKey={analyticsKey} />
          </>
        )}
        <style type="text/css">{`
      @font-face {
        font-family: 'Regular';
        src: url('${fonts.regular}') format('opentype');
      }
      ${
        hasFontAvailable(fonts.bold)
          ? `@font-face {
            font-family: 'Bold';
            src: url('${fonts.bold}') format('opentype');
            }`
          : ''
      }
          ${
            hasFontAvailable(fonts.medium)
              ? `@font-face {
            font-family: 'Medium';
            src: url('${fonts.medium}') format('opentype');
            }`
              : ''
          }
          ${
            hasFontAvailable(fonts.light)
              ? `@font-face {
              font-family: 'Light';
        src: url('${fonts.light}') format('opentype');
        }`
              : ''
          }
        `}</style>
        <div className="App" style={appStyleVariables}>
          {/* <div
            style={{
              position: 'fixed',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'start',
              justifyContent: 'center',
              color: 'red',
              zIndex: '10000',
            }}
          >
            <span>{quote.id ? quote.id : 'null'}</span>
            <span>properties: </span>
            {quote.properties?.map(({ namespace, data }, index) => (
              <span key={namespace + index}>{`${namespace}: ${data}`}</span>
            ))}
          </div> */}
          <Routes>
            <Route path="/" element={<AppLayout />}>
              {routes.map((route, idx) => {
                return (
                  route.component && (
                    <Route
                      key={idx}
                      exact={route.exact}
                      path={route.path}
                      name={route.name}
                      element={<route.component />}
                    />
                  )
                );
              })}
            </Route>
          </Routes>
        </div>
      </BrowserRouter>
    </>
  );
}

export default App;
